import React, { Component } from 'react'
import { connect } from 'react-redux'
import { withI18n, withI18nProps } from '@lingui/react'
import compose from 'lodash/fp/compose'

import errorBoundary from 'client/shared/decorators/error-boundary'

import SimpleFormat from 'client/shared/blocks/simple-format'
import { Card } from 'client/bookmate/blocks/card'
import { CommentReply } from 'client/bookmate/blocks/comment-reply'

import {
  throttledToggleLike as likeComment,
  remove as removeComment,
} from 'client/bookmate/reducers/comments-reducer'

import { report as reportProblem } from 'client/bookmate/reducers/problem-form-reducer'

import DeleteIcon from 'client/shared/icons/delete.svg'
import ReportIcon from 'client/shared/icons/exclam.svg'
import { CurrentUserState } from 'client/shared/types/current-user'
import { Dispatch, State } from 'shared/types/redux'

type Props = {
  comment: Record<string, any>
  resourceType: 'quotes' | 'impressions' | 'posts'
  resourceUuid: string
  onReplyClick: () => void
  active?: boolean
  currentUser: CurrentUserState
  showAuthPopup: (arg) => void
  dispatch: Dispatch
} & withI18nProps

class _CardCommentBox extends Component<Props> {
  static defaultProps = {
    active: false,
  }

  render(): JSX.Element {
    const {
      comment,
      active,
      currentUser: { auth: isLoggedIn },
      showAuthPopup,
    } = this.props

    const header = {
      user: comment.creator,
      path: `#comment-${comment.uuid}`,
      date: comment.created_at * 1000,
      isLoggedIn,
      showAuthPopup,
    }
    const footer = {
      likeCount: comment.likes_count,
      liked: comment.liked,
      menuData: this.menuData,
    }

    return (
      <Card
        kind="thread"
        active={active}
        anchor={`comment-${comment.uuid}`}
        footer={footer}
        header={header}
        onReplyClick={this.handleReplyClick}
        onLikeClick={this.toggleLike}
      >
        {comment.parent && <CommentReply {...comment.parent} />}
        <SimpleFormat text={comment.content} element="p" withLinks />
      </Card>
    )
  }

  handleReplyClick = () => {
    const { onReplyClick, comment } = this.props

    onReplyClick(comment)
  }

  toggleLike = () => {
    const {
      currentUser: { auth },
      comment: { uuid, liked, likes_count },
      dispatch,
      showAuthPopup,
    } = this.props

    if (!auth) {
      return dispatch(
        showAuthPopup({
          callback: () => likeComment({ uuid, liked, likes_count, dispatch }),
        }),
      )
    }

    likeComment({ uuid, liked, likes_count, dispatch })
  }

  get isMine() {
    const {
      currentUser: { data: currentUser },
      comment,
    } = this.props

    return comment.creator && comment.creator.login === currentUser.login
  }

  get menuData() {
    const { i18n } = this.props

    let item
    if (this.isMine) {
      item = {
        icon: DeleteIcon,
        title: i18n.t`shared.remove`,
        action: this.onRemoveClick,
      }
    } else {
      item = {
        icon: ReportIcon,
        title: i18n.t`context-menu.complain`,
        action: this.onComplainClick,
      }
    }

    const items = [item]

    return {
      position: 'top-right',
      items,
    }
  }

  onRemoveClick = () => {
    const {
      comment: { uuid },
      resourceUuid,
      i18n,
      dispatch,
    } = this.props

    if (confirm(i18n.t`context-menu.comment_remove_confirm`)) {
      dispatch(
        removeComment({
          uuid,
          resourceUuid,
        }),
      )
    }
  }

  onComplainClick = () => {
    const {
      comment: { uuid },
      dispatch,
    } = this.props

    dispatch(
      reportProblem({
        content_problem: {
          problem_kind: 'complaint',
          problem_type: 'text',
          resource_type: 'comment',
          resource_uuid: uuid,
        },
      }),
    )
  }
}

const wrappers = compose(
  connect((state: State) => ({
    currentUser: state.currentUser,
  })),
  errorBoundary,
  withI18n({ update: true }),
)

export const CardCommentBox = wrappers(_CardCommentBox)
